import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Upload, Loader2, CheckCircle, AlertTriangle,
  Trash2, ChevronDown, ChevronUp, RefreshCw, UserPlus, Ban, ArrowRightLeft, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../api/client';

/* ─── Entity configuration ─── */

interface EntityConfig {
  displayName: string;
  displayNamePlural: string;
  returnPath: string;
  parseEndpoint: string;
  confirmEndpoint: string;
  templateColumns: string[];
  summaryFields: (item: any) => { label: string; sublabel: string; initials: string };
}

const ENTITY_CONFIGS: Record<string, EntityConfig> = {
  employees: {
    displayName: 'Employee',
    displayNamePlural: 'Team Members',
    returnPath: '/team',
    parseEndpoint: '/users/import',
    confirmEndpoint: '/users/import/confirm',
    templateColumns: ['First Name', 'Last Name', 'Email', 'Phone', 'Role', 'Start Date'],
    summaryFields: (item: any) => ({
      label: [item.firstName, item.lastName].filter(Boolean).join(' ') || 'Unknown',
      sublabel: item.email || 'No email',
      initials: ((item.firstName?.[0] || '') + (item.lastName?.[0] || '')).toUpperCase() || '?',
    }),
  },
  drivers: {
    displayName: 'Driver',
    displayNamePlural: 'Drivers',
    returnPath: '/trucking/drivers',
    parseEndpoint: '/trucking/drivers/import',
    confirmEndpoint: '/trucking/drivers/import/confirm',
    templateColumns: ['First Name', 'Last Name', 'Email', 'Phone', 'License Class', 'License Number', 'License Expiry', 'Hire Date', 'Assigned Truck'],
    summaryFields: (item: any) => ({
      label: [item.firstName || item.first_name, item.lastName || item.last_name].filter(Boolean).join(' ') || 'Unknown',
      sublabel: item.email || item.phone || 'No contact',
      initials: ((item.firstName?.[0] || item.first_name?.[0] || '') + (item.lastName?.[0] || item.last_name?.[0] || '')).toUpperCase() || '?',
    }),
  },
  fleet: {
    displayName: 'Fleet Unit',
    displayNamePlural: 'Fleet Units',
    returnPath: '/trucking/fleet',
    parseEndpoint: '/trucking/fleet/import',
    confirmEndpoint: '/trucking/fleet/import/confirm',
    templateColumns: ['Unit Number', 'Type', 'Year', 'Make', 'Model', 'VIN', 'Plate'],
    summaryFields: (item: any) => ({
      label: item.unit_number || item.unitNumber || 'Unknown',
      sublabel: [item.year, item.make, item.model].filter(Boolean).join(' ') || item.type || '',
      initials: (item.unit_number || item.unitNumber || '??').slice(0, 2).toUpperCase(),
    }),
  },
  clients: {
    displayName: 'Client',
    displayNamePlural: 'Clients',
    returnPath: '/customers',
    parseEndpoint: '/customers/import',
    confirmEndpoint: '/customers/import/confirm',
    templateColumns: ['Company Name', 'Contact Name', 'Email', 'Phone', 'Address', 'City', 'Province', 'Postal Code'],
    summaryFields: (item: any) => ({
      label: item.company_name || item.companyName || item.name || 'Unknown',
      sublabel: item.email || item.contact_name || item.contactName || '',
      initials: (item.company_name || item.companyName || item.name || '??').slice(0, 2).toUpperCase(),
    }),
  },
  equipment: {
    displayName: 'Equipment',
    displayNamePlural: 'Equipment',
    returnPath: '/equipment',
    parseEndpoint: '/equipment/import',
    confirmEndpoint: '/equipment/import/confirm',
    templateColumns: ['Name', 'Type', 'Serial Number', 'Year', 'Make', 'Model', 'Status'],
    summaryFields: (item: any) => ({
      label: item.name || item.unit_number || 'Unknown',
      sublabel: item.type || item.serial_number || '',
      initials: (item.name || item.unit_number || '??').slice(0, 2).toUpperCase(),
    }),
  },
  'rate-sheets': {
    displayName: 'Rate',
    displayNamePlural: 'Rate Sheets',
    returnPath: '/rates',
    parseEndpoint: '/rate-sheets/import',
    confirmEndpoint: '/rate-sheets/import/confirm',
    templateColumns: ['Service Name', 'Unit', 'Rate', 'Currency', 'Notes'],
    summaryFields: (item: any) => ({
      label: item.service_name || item.serviceName || item.name || 'Unknown',
      sublabel: item.rate ? `$${item.rate}/${item.unit || 'ea'}` : '',
      initials: (item.service_name || item.serviceName || item.name || '??').slice(0, 2).toUpperCase(),
    }),
  },
  'rental-assets': {
    displayName: 'Asset',
    displayNamePlural: 'Rental Equipment',
    returnPath: '/rentals/assets',
    parseEndpoint: '/surface-rentals/assets/import',
    confirmEndpoint: '/surface-rentals/assets/import/confirm',
    templateColumns: ['Asset Number', 'Name', 'Category', 'Daily Rate', 'Status', 'Location', 'Make', 'Model', 'Serial Number'],
    summaryFields: (item: any) => ({
      label: item.name || item.assetNumber || 'Unknown',
      sublabel: [
        item.category,
        item.dailyRate != null ? `$${item.dailyRate}/day` : null,
      ].filter(Boolean).join(' · '),
      initials: (item.name || item.assetNumber || '??').slice(0, 2).toUpperCase(),
    }),
  },
};

/* ─── HITL import types (employees + drivers) ─── */

type ImportStatus = 'new' | 'skipped_existing' | 'will_reactivate' | 'possible_match' | 'invalid';

interface MatchCandidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  employmentStatus?: string;
  licenseNumber?: string | null;
  status?: string;
}

interface ConfirmResultItem {
  name: string;
  result: string;
  id?: string;
  email?: string;
  reason?: string;
}

interface ConfirmWarning {
  name: string;
  warning: string;
}

interface DuplicateSheetWarning {
  id: string;
  name: string;
  customerId: string | null;
  effectiveDate: string | null;
  status: string;
  itemCount: number;
}

/* ─── Component ─── */

type ImportState = 'idle' | 'uploading' | 'preview' | 'saving' | 'done';

export default function ImportScreen() {
  const { entityType } = useParams<{ entityType: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [importState, setImportState] = useState<ImportState>('idle');
  const [items, setItems] = useState<any[]>([]);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [parseMessage, setParseMessage] = useState<string | null>(null);
  // Frozen at parse time — rows can be removed below, and "N items found" has
  // to keep describing the document rather than the current edit state.
  const [parsedCount, setParsedCount] = useState(0);
  const [parseTruncated, setParseTruncated] = useState(false);
  const [parseDiagnostics, setParseDiagnostics] = useState<{
    reason?: string;
    detectedColumns?: string[];
    trimmedColumnCount?: number;
  } | null>(null);
  const [result, setResult] = useState<{ created: any[]; reactivated: any[]; skipped: any[]; counts?: { inserted: number; skipped: number } } | null>(null);
  // HITL resolution state for employee possible_match rows
  const [resolutions, setResolutions] = useState<Record<number, string>>({});
  // New confirm response format (results + warnings)
  const [confirmResults, setConfirmResults] = useState<ConfirmResultItem[] | null>(null);
  const [confirmWarnings, setConfirmWarnings] = useState<ConfirmWarning[]>([]);
  // Rate sheet duplicate warning from parse response
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateSheetWarning[] | null>(null);

  const config = entityType ? ENTITY_CONFIGS[entityType] : undefined;

  /* ── Mutations (must be before any early returns) ── */

  const importMutation = useMutation({
    mutationFn: (file: File) => {
      if (!config) throw new Error('No config');
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post(config.parseEndpoint, formData, { timeout: 120000 });
    },
    onSuccess: (res: any) => {
      if (!config) return;
      const body = res.data;
      const parsed = body.data || body;
      const rawItems = parsed?.items ?? [];
      const safeItems = Array.isArray(rawItems) ? rawItems : [];
      setItems(safeItems);
      setParseMessage(body.message || null);

      // `truncated` / `parsedCount` ride the envelope as SIBLINGS of `data`;
      // the rate-sheet parser also stamps them onto the payload. Check the
      // envelope first and fall back — see `duplicateWarning` below, which
      // reads only the payload and has therefore never rendered.
      setParseTruncated((body.truncated ?? parsed?.truncated) === true);
      const count = body.parsedCount ?? parsed?.parsedCount;
      // Absent on importers that don't report a count — fall back to the rows
      // we actually received rather than rendering a confident 0.
      setParsedCount(Number.isFinite(count) ? Number(count) : safeItems.length);

      setParseDiagnostics({
        reason: parsed?.reason,
        detectedColumns: parsed?.detectedColumns,
        trimmedColumnCount: parsed?.trimmedColumnCount,
      });
      // Rate sheet duplicate warning
      setDuplicateWarning(
        Array.isArray(parsed?.duplicateWarning) && parsed.duplicateWarning.length > 0
          ? parsed.duplicateWarning
          : null,
      );
      setImportError(null);
      setImportState('preview');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || err.message || 'Failed to parse file';
      setImportError(msg);
      setImportState('idle');
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (args?: { action?: string }) => {
      if (!config) throw new Error('No config');
      const isHitl = entityType === 'employees' || entityType === 'drivers';
      const isRateSheetImport = entityType === 'rate-sheets';
      if (isHitl) {
        // Build items with resolution field for possible_match rows, excluding invalid
        const resolvedItems = items
          .map((item, idx) => {
            if (item.importStatus === 'invalid') return null;
            if (item.importStatus === 'possible_match' && resolutions[idx]) {
              return { ...item, resolution: resolutions[idx] };
            }
            return item;
          })
          .filter(Boolean);
        return apiClient.post(config.confirmEndpoint, { items: resolvedItems });
      }
      if (isRateSheetImport && args?.action) {
        return apiClient.post(config.confirmEndpoint, { items, action: args.action });
      }
      return apiClient.post(config.confirmEndpoint, { items });
    },
    onSuccess: (res: any) => {
      const data = res.data.data || res.data;
      const isHitl = entityType === 'employees' || entityType === 'drivers';
      const isRateSheetImport = entityType === 'rate-sheets';

      if (isHitl && Array.isArray(data.results)) {
        // New confirm response format with results + warnings
        setConfirmResults(data.results);
        setConfirmWarnings(Array.isArray(data.warnings) ? data.warnings : []);

        // Check if any result needs resolution (defensive)
        const needsResolution = data.results.filter((r: ConfirmResultItem) => r.result === 'needs_resolution');
        if (needsResolution.length > 0) {
          setImportError(`${needsResolution.length} row(s) still need resolution: ${needsResolution.map((r: ConfirmResultItem) => r.name).join(', ')}`);
          setImportState('preview');
          return;
        }

        // Derive legacy result shape from new results for the done screen
        const inserted = data.results.filter((r: ConfirmResultItem) => r.result === 'inserted');
        const updated = data.results.filter((r: ConfirmResultItem) => r.result === 'updated');
        const reactivated = data.results.filter((r: ConfirmResultItem) => r.result === 'reactivated');
        const skipped = data.results.filter((r: ConfirmResultItem) => r.result === 'skipped');
        setResult({
          created: [...inserted, ...updated],
          reactivated,
          skipped,
          counts: { inserted: inserted.length + updated.length, skipped: skipped.length },
        });
      } else if (isRateSheetImport && data.result) {
        // Rate sheet confirm response: { result, rateSheetId?, reason? }
        if (data.result === 'cancelled') {
          toast('Import cancelled');
          resetImport();
          return;
        }
        if (data.result === 'created') {
          toast.success('Rate sheet created');
          queryClient.invalidateQueries({ queryKey: ['rate-sheets'] });
          setResult({
            created: [{ name: data.rateSheetId || 'New rate sheet' }],
            reactivated: [],
            skipped: [],
            counts: { inserted: 1, skipped: 0 },
          });
          setImportState('done');
          return;
        }
        // needs_confirmation — shouldn't reach onSuccess (it's 409), but handle defensively
        if (data.result === 'needs_confirmation') {
          setImportError(data.reason || 'A duplicate rate sheet exists — please choose an action above.');
          setImportState('preview');
          return;
        }
        // Fallback for unknown result
        setResult({
          created: data.rateSheetId ? [{ name: data.rateSheetId }] : [],
          reactivated: [],
          skipped: [],
        });
      } else {
        // Legacy confirm response (non-employee entities)
        setResult({
          created: Array.isArray(data.created) ? data.created : (Array.isArray(data.inserted) ? data.inserted : []),
          reactivated: Array.isArray(data.reactivated) ? data.reactivated : [],
          skipped: Array.isArray(data.skipped) ? data.skipped : [],
          counts: data.counts || undefined,
        });
      }
      setImportState('done');
      queryClient.invalidateQueries({ queryKey: [entityType] });
      if (entityType === 'employees') {
        queryClient.invalidateQueries({ queryKey: ['users'] });
      }
      if (entityType === 'drivers') {
        queryClient.invalidateQueries({ queryKey: ['trucking-drivers'] });
      }
    },
    onError: (err: any) => {
      const isRateSheetImport = entityType === 'rate-sheets';
      // Handle 409 needs_confirmation for rate sheets
      if (isRateSheetImport && err.response?.status === 409) {
        const data = err.response.data?.data || err.response.data;
        if (data?.result === 'needs_confirmation') {
          // Re-present the duplicate warning — admin must choose
          if (Array.isArray(data.duplicateWarning) && data.duplicateWarning.length > 0) {
            setDuplicateWarning(data.duplicateWarning);
          }
          setImportError(data.reason || 'A duplicate rate sheet exists — please choose an action.');
          setImportState('preview');
          return;
        }
      }
      setImportError(err.response?.data?.error || `Failed to save ${config?.displayNamePlural?.toLowerCase() || 'records'}`);
      setImportState('preview');
    },
  });

  /* ── Unknown entity guard ── */
  if (!config || !entityType) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark flex flex-col items-center justify-center gap-4">
        <AlertTriangle size={48} className="text-amber-400" />
        <h1 className="text-xl font-bold text-white">Unknown Import Type</h1>
        <p className="text-fieldiq-muted text-sm">
          The import type <span className="text-white font-mono">"{entityType || ''}"</span> is not supported.
        </p>
        <button
          onClick={() => navigate('/')}
          className="mt-2 text-fieldiq-accent hover:text-amber-400 text-sm font-semibold"
        >
          Go Home
        </button>
      </div>
    );
  }

  /* ── Handlers ── */

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    setImportState('uploading');
    setImportError(null);
    importMutation.mutate(file);
  }

  function resetImport() {
    setImportState('idle');
    setItems([]);
    setExpandedRow(null);
    setImportError(null);
    setParseMessage(null);
    setParsedCount(0);
    setParseTruncated(false);
    setParseDiagnostics(null);
    setResult(null);
    setResolutions({});
    setConfirmResults(null);
    setConfirmWarnings([]);
    setDuplicateWarning(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeItem(index: number) {
    setItems(prev => prev.filter((_, i) => i !== index));
    // Reindex resolutions when a row is removed
    setResolutions(prev => {
      const next: Record<number, string> = {};
      for (const [k, v] of Object.entries(prev)) {
        const ki = Number(k);
        if (ki === index) continue;
        next[ki > index ? ki - 1 : ki] = v;
      }
      return next;
    });
    if (expandedRow === index) setExpandedRow(null);
    else if (expandedRow !== null && expandedRow > index) setExpandedRow(expandedRow - 1);
  }

  function setResolution(index: number, value: string) {
    setResolutions(prev => ({ ...prev, [index]: value }));
  }

  function handleConfirm() {
    setImportState('saving');
    setImportError(null);
    confirmMutation.mutate({});
  }

  function handleRateSheetConfirm(action: 'create_new' | 'cancel') {
    if (action === 'cancel') {
      // Can cancel client-side or via API; using API so the server knows
      setImportState('saving');
      setImportError(null);
      confirmMutation.mutate({ action: 'cancel' });
    } else {
      setImportState('saving');
      setImportError(null);
      confirmMutation.mutate({ action: 'create_new' });
    }
  }

  function downloadTemplate() {
    const csv = config!.templateColumns.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entityType}-import-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const disabled = importState === 'saving';
  const isEmployeeImport = entityType === 'employees';
  const isDriverImport = entityType === 'drivers';
  const isHitlImport = isEmployeeImport || isDriverImport;
  const isRateSheetImport = entityType === 'rate-sheets';
  const hasDuplicateWarning = isRateSheetImport && duplicateWarning !== null && duplicateWarning.length > 0;

  // HITL: count possible_match rows needing resolution (employees + drivers)
  const possibleMatchRows = isHitlImport
    ? items.reduce<number[]>((acc, item, i) => {
        if (item.importStatus === 'possible_match') acc.push(i);
        return acc;
      }, [])
    : [];
  const unresolvedCount = possibleMatchRows.filter(i => !resolutions[i]).length;
  const invalidCount = isHitlImport ? items.filter(item => item.importStatus === 'invalid').length : 0;
  const confirmableCount = isHitlImport
    ? items.filter(item => item.importStatus !== 'invalid').length
    : items.length;

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      {/* Header */}
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              aria-label="Back"
              className="text-white hover:text-amber-400 hover:bg-white/10 p-1 rounded transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-foreground">
              Import {config.displayNamePlural}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-6">
        {/* ── IDLE: Upload area ── */}
        {importState === 'idle' && (
          <div className="space-y-4">
            {/* Moved out of the header when it took the one-line gold-standard
                form; the copy is entity-specific, so it stays. */}
            <p className="text-sm text-fieldiq-text">
              Upload a spreadsheet, PDF, or document to bulk-add{' '}
              {config.displayNamePlural.toLowerCase()}
            </p>

            {importError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl p-4 text-sm">
                {importError}
              </div>
            )}

            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-fieldiq-border rounded-2xl p-12 text-center cursor-pointer hover:border-fieldiq-accent/50 transition-colors"
            >
              <Upload size={40} className="mx-auto text-fieldiq-muted mb-4" />
              <p className="text-white font-semibold mb-1">Tap to upload a file</p>
              <p className="text-fieldiq-muted text-sm">
                Supports: .xlsx, .xls, .csv, .pdf, .docx
              </p>
              <p className="text-fieldiq-muted text-xs mt-2">Max 10 MB</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,.pdf,.docx"
              onChange={handleFileChange}
              className="hidden"
            />

            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-white">Tips</h3>
                <button
                  onClick={downloadTemplate}
                  className="text-xs text-fieldiq-accent hover:text-amber-400 font-medium transition-colors"
                >
                  Download Template
                </button>
              </div>
              <ul className="text-xs text-fieldiq-muted space-y-1.5">
                <li>Include columns: {config.templateColumns.join(', ')}</li>
                <li>AI will do its best to match your columns automatically</li>
                <li>Duplicates will be skipped during import</li>
                <li>You can review and remove items before confirming</li>
              </ul>
            </div>
          </div>
        )}

        {/* ── UPLOADING: Spinner ── */}
        {importState === 'uploading' && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 size={44} className="animate-spin text-fieldiq-accent" />
            <p className="text-white font-medium">Parsing {config.displayNamePlural.toLowerCase()} with AI...</p>
            <p className="text-fieldiq-muted text-sm">This may take 10-30 seconds</p>
          </div>
        )}

        {/* ── PREVIEW / SAVING: Card list ── */}
        {(importState === 'preview' || importState === 'saving') && (
          <div className="space-y-4">
            {importError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl p-4 text-sm">
                {importError}
              </div>
            )}

            {/* Suppressed when the parse was cut off — the API packs the "this
                list is INCOMPLETE" sentence into the same `message`, and
                rendering that in success-green under a warning banner tells
                the reader two different things at once. */}
            {parseMessage && !parseTruncated && (
              <div className="bg-green-500/10 border border-green-500/30 text-green-400 rounded-xl p-4 text-sm">
                {parseMessage}
              </div>
            )}

            {/* Truncated parse — the import is still allowed to proceed on the
                partial list; this only makes the gap visible. */}
            {parseTruncated && (
              <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-xl p-4 text-sm flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">
                    This document was too large to fully process. Only {parsedCount} item
                    {parsedCount !== 1 ? 's' : ''} were extracted.
                  </p>
                  <p className="text-amber-400/80 text-xs mt-1">
                    Split the file into smaller sections and import each part separately.
                  </p>
                </div>
              </div>
            )}

            {/* No count line here on purpose — the list header below already
                renders "N rate sheets found". A second one sat four lines
                above it showing a different number the moment a row was
                deleted, because that one counts `items` live and this one was
                frozen at parse time. The truncation banner carries the parsed
                count; the list header carries what will actually be saved. */}

            {/* HITL: unresolved count banner */}
            {isHitlImport && unresolvedCount > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-xl p-3 text-sm flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <span>
                  {unresolvedCount} row{unresolvedCount !== 1 ? 's' : ''} need{unresolvedCount === 1 ? 's' : ''} review — resolve before confirming.
                </span>
              </div>
            )}

            {/* Rate sheet duplicate warning */}
            {hasDuplicateWarning && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <Copy size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-amber-400 font-medium text-sm">
                      Duplicate rate sheet{duplicateWarning!.length > 1 ? 's' : ''} found
                    </p>
                    <p className="text-fieldiq-muted text-xs mt-1">
                      Rate sheets are versioned — creating a new version with the same name is normal if the effective date differs.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {duplicateWarning!.map((dupe) => (
                    <div key={dupe.id} className="bg-fieldiq-dark/60 rounded-lg px-3 py-2 flex items-center justify-between">
                      <div className="text-sm">
                        <span className="text-white font-medium">{dupe.name}</span>
                        <span className="text-fieldiq-muted ml-2 text-xs">
                          {dupe.customerId ? `Customer-specific` : 'All customers'}
                        </span>
                        {dupe.effectiveDate && (
                          <span className="text-fieldiq-muted ml-2 text-xs">
                            Effective: {dupe.effectiveDate}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${
                          dupe.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          dupe.status === 'draft' ? 'bg-fieldiq-dark text-fieldiq-muted' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          {dupe.status}
                        </span>
                        <span className="text-fieldiq-muted">{dupe.itemCount} item{dupe.itemCount !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => handleRateSheetConfirm('create_new')}
                    disabled={disabled}
                    className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark text-sm font-semibold px-4 py-2 rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
                  >
                    {disabled ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    Create as new version
                  </button>
                  <button
                    onClick={() => handleRateSheetConfirm('cancel')}
                    disabled={disabled}
                    className="flex items-center gap-1.5 text-sm text-fieldiq-muted hover:text-white px-3 py-2 rounded-lg border border-fieldiq-border hover:border-fieldiq-muted transition-colors disabled:opacity-50"
                  >
                    <Ban size={14} />
                    Cancel import
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-fieldiq-muted text-sm">
                {items.length} {items.length === 1 ? config.displayName.toLowerCase() : config.displayNamePlural.toLowerCase()} found
                {isHitlImport && invalidCount > 0 && (
                  <span className="text-red-400"> ({invalidCount} invalid)</span>
                )}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={resetImport}
                  disabled={disabled}
                  className="text-sm text-fieldiq-muted hover:text-white px-3 py-1.5 rounded-lg border border-fieldiq-border hover:border-fieldiq-muted transition-colors disabled:opacity-50"
                >
                  Start Over
                </button>
                {/* Hide normal confirm when rate-sheet dupe warning is showing (actions are in the warning panel) */}
                {!hasDuplicateWarning && (
                  <button
                    onClick={handleConfirm}
                    disabled={disabled || confirmableCount === 0 || unresolvedCount > 0}
                    className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark text-sm font-semibold px-4 py-2 rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
                  >
                    {disabled ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {disabled ? 'Importing...' : `Import ${confirmableCount}`}
                  </button>
                )}
              </div>
            </div>

            {/* Item cards */}
            <div className="space-y-3">
              {items.map((item, i) => {
                const isExpanded = expandedRow === i;
                const summary = config.summaryFields(item);
                const status: ImportStatus | undefined = item.importStatus;
                const isInvalid = status === 'invalid';
                const isPossibleMatch = status === 'possible_match';
                const candidates: MatchCandidate[] | undefined = item.candidates;
                const currentResolution = resolutions[i];

                return (
                  <div
                    key={i}
                    className={`bg-fieldiq-card border rounded-xl overflow-hidden ${
                      isInvalid ? 'border-red-500/30 opacity-60' :
                      isPossibleMatch && !currentResolution ? 'border-amber-500/40' :
                      'border-fieldiq-border'
                    }`}
                  >
                    {/* Summary row */}
                    <div
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02]"
                      onClick={() => setExpandedRow(isExpanded ? null : i)}
                    >
                      <div className="w-9 h-9 rounded-full bg-amber-700/40 flex items-center justify-center text-amber-300 font-bold text-xs flex-shrink-0">
                        {summary.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium text-sm truncate ${isInvalid ? 'text-fieldiq-muted' : 'text-white'}`}>{summary.label}</span>
                          {/* Status badge */}
                          {isHitlImport && status && <ImportStatusBadge status={status} />}
                        </div>
                        <div className="flex items-center gap-2 text-xs mt-0.5">
                          <span className={`truncate ${item.email ? 'text-fieldiq-muted' : 'text-amber-400'}`}>
                            {item.email || '— no email'}
                          </span>
                          {item.phone && <span className="text-fieldiq-muted">{item.phone}</span>}
                          {isDriverImport && (item.licenseNumber || item.license_number) && (
                            <span className="text-fieldiq-muted">Lic: {item.licenseNumber || item.license_number}</span>
                          )}
                          {isDriverImport && (item.assignedTruck || item.assigned_truck) && (
                            <span className="text-fieldiq-muted">Truck: {item.assignedTruck || item.assigned_truck}</span>
                          )}
                          {!isDriverImport && item.role && <span className="text-fieldiq-muted">{item.role}</span>}
                        </div>
                        {isInvalid && item.importReason && (
                          <p className="text-red-400 text-xs mt-1">{item.importReason}</p>
                        )}
                      </div>
                      {!isInvalid && (
                        <button
                          onClick={(e) => { e.stopPropagation(); removeItem(i); }}
                          className="text-fieldiq-muted hover:text-red-400 p-1"
                          disabled={disabled}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      {isExpanded
                        ? <ChevronUp size={16} className="text-fieldiq-muted" />
                        : <ChevronDown size={16} className="text-fieldiq-muted" />
                      }
                    </div>

                    {/* HITL: Resolution controls for possible_match */}
                    {isPossibleMatch && candidates && candidates.length > 0 && (
                      <div className="border-t border-amber-500/20 bg-amber-500/5 px-4 py-3 space-y-2">
                        <p className="text-xs text-amber-400 font-medium">
                          Possible match{candidates.length > 1 ? 'es' : ''} found — choose an action:
                        </p>
                        {candidates.map((c) => (
                          <div key={c.id} className="flex items-center justify-between bg-fieldiq-dark/60 rounded-lg px-3 py-2">
                            <div className="text-sm">
                              <span className="text-white font-medium">{c.firstName} {c.lastName}</span>
                              {isDriverImport ? (
                                <>
                                  <span className="text-fieldiq-muted ml-2 text-xs">
                                    Lic: {c.licenseNumber || 'no licence on file'}
                                  </span>
                                  {c.email && <span className="text-fieldiq-muted ml-2 text-xs">{c.email}</span>}
                                  <span className={`ml-2 text-xs ${
                                    c.status === 'active' ? 'text-green-400' :
                                    c.status === 'inactive' ? 'text-red-400' : 'text-fieldiq-muted'
                                  }`}>
                                    {c.status || 'unknown'}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="text-fieldiq-muted ml-2 text-xs">{c.email || 'no email'}</span>
                                  <span className={`ml-2 text-xs ${
                                    c.employmentStatus === 'active' ? 'text-green-400' :
                                    c.employmentStatus === 'terminated' ? 'text-red-400' : 'text-fieldiq-muted'
                                  }`}>
                                    {c.employmentStatus}
                                  </span>
                                </>
                              )}
                            </div>
                            <button
                              onClick={() => setResolution(i, `update_existing:${c.id}`)}
                              disabled={disabled}
                              className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                                currentResolution === `update_existing:${c.id}`
                                  ? 'bg-blue-500 text-white'
                                  : 'border border-fieldiq-border text-fieldiq-muted hover:text-white hover:border-blue-500'
                              }`}
                            >
                              <ArrowRightLeft size={12} className="inline mr-1" />
                              This is them
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => setResolution(i, 'create_new')}
                            disabled={disabled}
                            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                              currentResolution === 'create_new'
                                ? 'bg-green-600 text-white'
                                : 'border border-fieldiq-border text-fieldiq-muted hover:text-white hover:border-green-500'
                            }`}
                          >
                            <UserPlus size={12} className="inline mr-1" />
                            Create as new person
                          </button>
                          <button
                            onClick={() => setResolution(i, 'skip')}
                            disabled={disabled}
                            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                              currentResolution === 'skip'
                                ? 'bg-fieldiq-muted text-fieldiq-dark'
                                : 'border border-fieldiq-border text-fieldiq-muted hover:text-white hover:border-fieldiq-muted'
                            }`}
                          >
                            <Ban size={12} className="inline mr-1" />
                            Skip
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Expanded detail — read-only key/value pairs */}
                    {isExpanded && (
                      <div className="border-t border-fieldiq-border px-4 py-3">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          {Object.entries(item).map(([key, value]) => {
                            if (value === null || value === undefined || value === '') return null;
                            if (typeof value === 'object') return null;
                            // Hide internal status fields from the detail view
                            if (['importStatus', 'importReason', 'existingUserId', 'existingDriverId'].includes(key)) return null;
                            const displayKey = key
                              .replace(/_/g, ' ')
                              .replace(/([A-Z])/g, ' $1')
                              .replace(/^\w/, c => c.toUpperCase())
                              .trim();
                            return (
                              <div key={key} className="text-xs">
                                <span className="text-fieldiq-muted">{displayKey}:</span>{' '}
                                <span className="text-white">{String(value)}</span>
                              </div>
                            );
                          })}
                        </div>
                        {/* notes / attributes (unmapped columns from the API) */}
                        {(() => {
                          const extra: Record<string, unknown> = item.notes || item.attributes;
                          if (!extra || typeof extra !== 'object') return null;
                          const entries = Object.entries(extra).filter(
                            ([, v]) => v !== null && v !== undefined && v !== '',
                          );
                          if (entries.length === 0) return null;
                          return (
                            <div className="border-t border-fieldiq-border mt-3 pt-3">
                              <p className="text-xs text-fieldiq-muted font-medium mb-2">Additional Fields</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                {entries.map(([k, v]) => (
                                  <div key={k} className="text-xs">
                                    <span className="text-fieldiq-muted">{k}:</span>{' '}
                                    <span className="text-white">{String(v)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {items.length === 0 && (
              <div className="bg-fieldiq-card border border-amber-500/30 rounded-xl p-6 text-sm space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-amber-400 font-medium">
                    {parseDiagnostics?.reason
                      ? parseDiagnostics.reason
                      : `No ${config.displayNamePlural.toLowerCase()} parsed. Try a different file.`}
                  </p>
                </div>
                {parseDiagnostics?.detectedColumns && parseDiagnostics.detectedColumns.length > 0 && (
                  <p className="text-fieldiq-muted">
                    <span className="text-fieldiq-text font-medium">Detected columns:</span>{' '}
                    {parseDiagnostics.detectedColumns.join(', ')}
                  </p>
                )}
                {parseDiagnostics?.trimmedColumnCount != null && parseDiagnostics.trimmedColumnCount > 20 && (
                  <p className="text-fieldiq-muted text-xs">
                    This file declared {parseDiagnostics.trimmedColumnCount} columns; if it still fails, re-saving as .xls often fixes an inflated column range.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── DONE: Results ── */}
        {importState === 'done' && result && (() => {
          const insertedCount = result.counts?.inserted ?? result.created.length;
          const skippedCount = result.counts?.skipped ?? result.skipped.length;
          const allSkipped = insertedCount === 0 && skippedCount > 0;

          return (
          <div className="space-y-6">
            <div className="flex flex-col items-center py-8">
              {allSkipped ? (
                <>
                  <AlertTriangle size={52} className="text-amber-400 mb-3" />
                  <h2 className="text-xl font-bold text-white mb-1">Nothing Imported</h2>
                  <p className="text-fieldiq-muted text-sm">
                    All {skippedCount} {skippedCount === 1 ? 'item' : 'items'} already exist in your fleet.
                  </p>
                </>
              ) : (
                <>
                  <CheckCircle size={52} className="text-green-400 mb-3" />
                  <h2 className="text-xl font-bold text-white mb-1">Import Complete</h2>
                  <p className="text-sm">
                    <span className="text-amber-500 font-semibold">{insertedCount} imported</span>
                    {result.reactivated.length > 0 && <span className="text-fieldiq-muted"> · {result.reactivated.length} reactivated</span>}
                    {skippedCount > 0 && <span className="text-fieldiq-muted"> · {skippedCount} skipped</span>}
                  </p>
                </>
              )}
            </div>

            {/* Warnings (from new confirm response) */}
            {confirmWarnings.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-1.5">
                  <AlertTriangle size={14} />
                  Warnings ({confirmWarnings.length})
                </h3>
                <div className="space-y-2">
                  {confirmWarnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-white font-medium flex-shrink-0">{w.name}:</span>
                      <span className="text-amber-400">{w.warning}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed results (from new confirm response) */}
            {confirmResults && (
              <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
                <h3 className="text-sm font-semibold text-green-400 mb-3 flex items-center gap-1.5">
                  <CheckCircle size={14} />
                  Results ({confirmResults.length})
                </h3>
                <div className="space-y-2">
                  {confirmResults.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-white">{r.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        r.result === 'inserted' ? 'bg-green-500/20 text-green-400' :
                        r.result === 'updated' ? 'bg-blue-500/20 text-blue-400' :
                        r.result === 'reactivated' ? 'bg-blue-500/20 text-blue-400' :
                        r.result === 'skipped' ? 'bg-fieldiq-dark text-fieldiq-muted' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {r.result}{r.reason ? ` — ${r.reason}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legacy: Created / inserted list (non-employee or fallback) */}
            {!confirmResults && result.created.length > 0 && (
              <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
                <h3 className="text-sm font-semibold text-green-400 mb-3 flex items-center gap-1.5">
                  <CheckCircle size={14} />
                  Imported ({result.created.length})
                </h3>
                <div className="space-y-2">
                  {result.created.map((u: any, i: number) => {
                    const summary = config.summaryFields(u);
                    return (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-white">{summary.label}</span>
                        <span className="text-fieldiq-muted text-xs">{summary.sublabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Reactivated list (legacy) */}
            {!confirmResults && result.reactivated.length > 0 && (
              <div className="bg-fieldiq-card border border-green-500/30 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-green-400 mb-3 flex items-center gap-1.5">
                  <RefreshCw size={14} />
                  Reactivated ({result.reactivated.length})
                </h3>
                <div className="space-y-2">
                  {result.reactivated.map((r: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-white">{r.first_name || r.firstName || ''} {r.last_name || r.lastName || ''}</span>
                      <span className="text-green-400 text-xs">Reactivated from retired</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skipped list (legacy) */}
            {!confirmResults && result.skipped.length > 0 && (
              <div className="bg-fieldiq-card border border-amber-500/30 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-1.5">
                  <AlertTriangle size={14} />
                  Skipped — already in your fleet ({result.skipped.length})
                </h3>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {result.skipped.map((s: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-sm gap-2">
                      <span className="text-white truncate">{s.name}{s.serial_number ? ` (${s.serial_number})` : ''}</span>
                      <span className="text-fieldiq-muted text-xs flex-shrink-0">{s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={resetImport}
                className="flex-1 border border-fieldiq-border text-white py-2.5 rounded-xl text-sm font-medium hover:border-fieldiq-muted transition-colors"
              >
                Import More
              </button>
              <button
                onClick={() => navigate(config.returnPath)}
                className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-400 transition-colors"
              >
                Back to {config.displayNamePlural}
              </button>
            </div>
          </div>
          );
        })()}
      </div>
    </div>
  );
}

/* ─── Status badge for employee import HITL ─── */

const STATUS_BADGE_STYLES: Record<ImportStatus, { bg: string; text: string; label: string }> = {
  new: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'New' },
  skipped_existing: { bg: 'bg-fieldiq-dark', text: 'text-fieldiq-muted', label: 'Skipped – exists' },
  will_reactivate: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Will reactivate' },
  possible_match: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Needs review' },
  invalid: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Invalid' },
};

function ImportStatusBadge({ status }: { status: ImportStatus }) {
  const style = STATUS_BADGE_STYLES[status] || STATUS_BADGE_STYLES.new;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}
